/* Sentai dashboard reels (For organisations). The dashboard screens in sections/st-dashboard.liquid
   are live HTML (snippets/st-dash-scene.liquid), and a short scripted reel plays on the selected one
   while the section is on screen: figures count up, rows arrive, a pointer clicks through the
   dashboard. It follows the Home app reels (st-app-reels.js) and shares their rules: see
   docs/DASHBOARD_PANEL.md and docs/APP_SLIDER.md.

   st-theme.js's AppTabs stays the one owner of the selection. It calls reels.select() when the
   selection changes and reels call tabs.selectFeature() to move on by themselves. Each reel is an
   async function; cancelling it throws away its screen and mounts a fresh copy, so a reel always
   starts from the markup. The markup is each reel's end state; a reel sets up its first state in
   its opening lines. With reduced motion, each screen shows its Figma frame. */
(() => {
  'use strict';
  if (window.SentaiDashReels) return;
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedMotion = () => motionPreference.matches;
  const EASE_OUT = 'cubic-bezier(.22,1,.36,1)';
  const SPRING = 'cubic-bezier(.34,1.45,.64,1)';
  const HOLD_MS = 2600;          // a finished screen stays readable before the next reel

  class Cancelled extends Error {}

  // Reel helpers. `mode` is 'play'; 'end', where every step jumps to its end, for a reel a
  // visitor has stopped; or 'still', which also jumps but leaves each screen where Figma shows it,
  // for reduced motion.
  const director = (screen, signal, mode) => {
    const instant = mode !== 'play';
    const still = mode === 'still';
    const guard = () => { if (signal.aborted) throw new Cancelled(); };
    const finished = animation => animation.finished.then(() => guard(), () => { throw new Cancelled(); });
    const soft = promise => { promise.catch(() => {}); return promise; };
    const wait = ms => new Promise((resolve, reject) => {
      if (signal.aborted) return reject(new Cancelled());
      if (instant || ms <= 0) return resolve();
      const timer = setTimeout(resolve, ms);
      signal.addEventListener('abort', () => { clearTimeout(timer); reject(new Cancelled()); }, { once: true });
    });
    // One dashboard pixel in CSS pixels. Motion is given in it, so it scales with the window.
    const px = parseFloat(getComputedStyle(screen).fontSize) || 1;
    const $ = selector => screen.querySelector(selector);
    const $$ = selector => [...screen.querySelectorAll(selector)];
    const list = target => (target == null ? [] : typeof target === 'string' ? $$(target) : target instanceof Element ? [target] : [...target]).filter(Boolean);
    const play = (target, keyframes, options = {}) => {
      guard();
      if (instant) return Promise.resolve();
      const animations = list(target).map((element, i) => element.animate(keyframes, {
        fill: 'both', easing: EASE_OUT, ...options,
        duration: options.duration ?? 500,
        delay: (options.delay ?? 0) + i * (options.stagger ?? 0)
      }));
      return soft(Promise.all(animations.map(finished)));
    };
    // Content fades in as it rises. Nothing blurs: a filter puts text in a layer of its own that
    // some browsers draw soft under the page's zoom.
    const reveal = (target, options = {}) => play(target, [
      { opacity: 0, transform: `translateY(${(options.y ?? 10) * px}px) scale(${options.scale ?? 1})` },
      { opacity: 1, transform: 'none' }
    ], { duration: 600, ...options });
    const pop = (target, options = {}) => play(target, [
      { opacity: 0, transform: 'scale(.6)' }, { opacity: 1, transform: 'none' }
    ], { duration: 520, easing: SPRING, ...options });
    // Characters arrive at a typing pace, with a caret. The whole text is laid out from the start,
    // its untyped part transparent, so lines never re-wrap.
    const type = (element, text, options = {}) => soft((async () => {
      guard();
      element.textContent = text;
      if (instant) return;
      const typed = document.createTextNode('');
      const caret = document.createElement('span');
      caret.className = 'st-dui-caret-blink';
      const rest = document.createElement('span');
      rest.className = 'st-dui-type-rest';
      rest.textContent = text;
      element.replaceChildren(typed, caret, rest);
      for (let i = 0; i < text.length; i++) {
        typed.data = text.slice(0, i + 1);
        rest.textContent = text.slice(i + 1);
        await wait((options.speed ?? 30) * (/[,.?!]/.test(text[i]) ? 4 : 1) * (0.6 + Math.random() * 0.8));
      }
      element.textContent = text;
    })());
    // AI text writes itself word by word, each word fading in.
    const stream = async (element, options = {}) => {
      guard();
      const words = element.textContent.trim().split(/\s+/);
      element.textContent = '';
      const spans = words.map(word => {
        const span = document.createElement('span');
        span.textContent = word + ' ';
        element.append(span);
        return span;
      });
      await play(spans, [{ opacity: 0 }, { opacity: 1 }], { duration: 380, stagger: options.stagger ?? 40, easing: 'ease-out' });
      element.textContent = words.join(' ');
    };
    // Figures count up to the value the markup shows.
    const count = (element, options = {}) => {
      guard();
      const target = element.textContent.trim();
      if (instant) return Promise.resolve();
      const duration = options.duration ?? 900;
      return soft(new Promise((resolve, reject) => {
        const start = performance.now();
        const frame = now => {
          if (signal.aborted) return reject(new Cancelled());
          const t = Math.min(1, (now - start) / duration);
          element.textContent = String(Math.round(+target * (1 - Math.pow(1 - t, 3))));
          if (t < 1) requestAnimationFrame(frame); else { element.textContent = target; resolve(); }
        };
        requestAnimationFrame(frame);
      }));
    };
    // A mouse click: the pointer glides to the control, presses, and a ripple spreads. Positions
    // are fractions of the screen, so they hold at any size and under the page's zoom.
    const pointer = $('.st-dui-cursor');
    const at = (element, options = {}) => {
      const box = screen.getBoundingClientRect();
      const r = element.getBoundingClientRect();
      return [((r.left + r.width * (options.x ?? .5)) - box.left) / box.width * 100, ((r.top + r.height * (options.y ?? .5)) - box.top) / box.height * 100];
    };
    const click = async (target, options = {}) => {
      guard();
      const element = typeof target === 'string' ? $(target) : target;
      if (instant || !pointer) return;
      const [x, y] = at(element, options);
      const from = pointer.dataset.at ? JSON.parse(pointer.dataset.at) : null;
      pointer.dataset.at = JSON.stringify([x, y]);
      if (!from) {
        // The pointer comes in from below right, as a hand on a mouse would bring it.
        await play(pointer, [{ left: `${Math.min(x + 12, 98)}%`, top: `${Math.min(y + 18, 98)}%`, opacity: 0 }, { left: `${x}%`, top: `${y}%`, opacity: 1 }], { duration: 750, easing: 'cubic-bezier(.3,0,.2,1)' });
      } else {
        await play(pointer, [{ left: `${from[0]}%`, top: `${from[1]}%`, opacity: 1 }, { left: `${x}%`, top: `${y}%`, opacity: 1 }], { duration: options.travel ?? 800, easing: 'cubic-bezier(.65,0,.35,1)' });
      }
      await wait(120);
      const ripple = document.createElement('span');
      ripple.className = 'st-dui-ripple';
      ripple.style.left = `${x}%`; ripple.style.top = `${y}%`;
      pointer.before(ripple);
      play(ripple, [{ opacity: .6, transform: 'translate(-50%,-50%) scale(.3)' }, { opacity: 0, transform: 'translate(-50%,-50%) scale(1.6)' }], { duration: 550, easing: 'ease-out' })
        .then(() => ripple.remove(), () => ripple.remove());
      await play(pointer, [{ transform: 'none' }, { transform: 'scale(.82)' }, { transform: 'none' }], { duration: 260, easing: 'ease-in-out', composite: 'add', fill: 'none' });
    };
    const lift = () => soft((async () => {
      if (instant || !pointer || !pointer.dataset.at) return;
      const [x, y] = JSON.parse(pointer.dataset.at);
      await play(pointer, [{ left: `${x}%`, top: `${y}%`, opacity: 1 }, { left: `${x}%`, top: `${y}%`, opacity: 0 }], { duration: 400 });
    })());
    // Scroll smoothly. The still view keeps the page where Figma shows it.
    const scrollTo = (element, top, options = {}) => {
      guard();
      if (still) return Promise.resolve();
      top = Math.max(0, Math.min(top, element.scrollHeight - element.clientHeight));
      if (instant) { element.scrollTop = top; return Promise.resolve(); }
      const from = element.scrollTop;
      const duration = options.duration ?? 900;
      return soft(new Promise((resolve, reject) => {
        const start = performance.now();
        const frame = now => {
          if (signal.aborted) return reject(new Cancelled());
          const t = Math.min(1, (now - start) / duration);
          const e = t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
          element.scrollTop = from + (top - from) * e;
          if (t < 1) requestAnimationFrame(frame); else resolve();
        };
        requestAnimationFrame(frame);
      }));
    };
    // Where an element sits in a scroll container, in the container's own CSS pixels.
    const offset = (element, container) => {
      const box = container.getBoundingClientRect();
      const scale = box.height ? container.offsetHeight / box.height : 1;
      return (element.getBoundingClientRect().top - box.top) * scale + container.scrollTop;
    };
    // Scroll just far enough for an element's bottom to show, with a margin.
    const bringIntoView = (element, container, options = {}) => {
      const scale = container.getBoundingClientRect().height ? container.offsetHeight / container.getBoundingClientRect().height : 1;
      const bottom = offset(element, container) + element.getBoundingClientRect().height * scale + (options.margin ?? 24) * px;
      const top = bottom - container.clientHeight;
      return top > container.scrollTop ? scrollTo(container, top, options) : Promise.resolve();
    };
    // A modal opens over the whole dashboard.
    const open = async modal => {
      guard();
      modal.hidden = false;
      await Promise.all([
        play(modal, [{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'ease-out' }),
        play(modal.firstElementChild, [{ opacity: 0, transform: `translateY(${16 * px}px) scale(.98)` }, { opacity: 1, transform: 'none' }], { duration: 450 })
      ]);
    };
    return { $, $$, wait, play, reveal, pop, type, stream, count, click, lift, scrollTo, offset, bringIntoView, open, instant, still, px };
  };

  const twinkle = (d, icon) => d.play(icon, [
    { transform: 'scale(.4) rotate(-40deg)', opacity: .3 },
    { transform: 'scale(1.25) rotate(12deg)', opacity: 1, offset: .6 },
    { transform: 'none', opacity: 1 }
  ], { duration: 900, easing: 'ease-out' });
  const flip = async (d, rows, reorder) => {
    const before = new Map(rows.map(row => [row, row.getBoundingClientRect().top]));
    reorder();
    if (d.instant) return;
    await Promise.all(rows.map(row => {
      const shift = before.get(row) - row.getBoundingClientRect().top;
      return shift ? d.play(row, [{ transform: `translateY(${shift}px)` }, { transform: 'none' }], { duration: 700, easing: 'cubic-bezier(.65,0,.35,1)', fill: 'none' }) : null;
    }));
  };

  // The reels, one per screen in snippets/st-dash-scene.liquid.
  const reels = {
    // Dashboard - Movement: the four counts, then the people behind the first, and a prompt sent.
    alerts: {
      length: 10500,
      async play(d) {
        const page = d.$('[data-st-scroll]');
        const tiles = d.$$('.st-dui-tile');
        const rows = d.$$('[data-st-row]');
        const target = d.$('[data-st-target]');
        const prompted = target.querySelector('[data-st-prompted] .t14');
        prompted.textContent = '';
        await d.wait(300);
        d.reveal(tiles, { stagger: 130, y: 14 });
        await Promise.all(tiles.map((tile, i) => d.wait(i * 130 + 150).then(() => d.count(tile.querySelector('[data-st-count]'), { duration: 1000 }))));
        if (!d.still) {
          await d.wait(500);
          d.reveal(rows, { stagger: 110, y: 8, duration: 450, delay: 500 });
          await d.scrollTo(page, d.offset(d.$('[data-st-alerts]'), page) - 12 * d.px, { duration: 1200 });
          await d.wait(rows.length * 110 + 300);
        } else await d.reveal(rows);
        if (d.still) return;   // the still view is the Figma "Dashboard - Movement" screen
        await d.click(target.querySelector('[data-st-send]'));
        prompted.textContent = 'Just now';
        await d.pop(prompted, { duration: 500 });
        d.lift();
      }
    },

    // Users - Details: the day's summary writes itself, then the log, an entry at a time.
    person: {
      length: 11500,
      async play(d) {
        const page = d.$('[data-st-scroll]');
        const text = d.$('[data-st-stream]');
        await d.wait(400);
        await d.reveal(d.$('.st-dui-presence'), { duration: 450 });
        await twinkle(d, d.$('[data-st-sparkle]'));
        await d.reveal(text, { duration: 1, y: 0 });
        await d.stream(text, { stagger: 36 });
        await d.reveal(d.$('.st-dui-ai__more'), { duration: 400, y: 4 });
        await d.wait(300);
        await d.reveal(d.$$('[data-st-in]').filter(element => element.matches('.st-dui-feed__day')), { duration: 400 });
        for (const entry of d.$$('[data-st-entry]')) {
          await d.bringIntoView(entry, page, { duration: 800 });
          await d.reveal(entry, { y: 8, duration: 420 });
          await d.play(entry.querySelectorAll('[data-st-quote]'), [
            { opacity: 0, transform: `translateY(${8 * d.px}px) scale(.85)` }, { opacity: 1, transform: 'none' }
          ], { duration: 520, stagger: 380, easing: SPRING });
          await d.wait(250);
        }
      }
    },

    // Users: every device's status, then sorted so the ones that need a look come first.
    hubs: {
      length: 8000,
      async play(d) {
        const rows = d.$$('[data-st-row]');
        const tags = rows.map(row => row.querySelector('.st-dui-tag[data-st-in]'));
        await d.wait(300);
        d.reveal(rows, { stagger: 90, y: 8, duration: 450 });
        await d.wait(rows.length * 90 + 200);
        await d.pop(tags, { stagger: 110 });
        if (d.still) return;   // the still view is the Figma "Users" screen
        await d.wait(600);
        // Sorting by Hub brings the offline and muted devices to the top.
        const sort = d.$('[data-st-hub-sort]');
        await d.click(sort, { x: .35 });
        sort.querySelector('.st-dui-sort').classList.add('is-up');
        const rank = { Offline: 0, Muted: 1, Online: 2 };
        const holder = d.$('[data-st-rows]');
        await flip(d, rows, () => [...rows].sort((a, b) => rank[a.dataset.stHub] - rank[b.dataset.stHub]).forEach(row => holder.append(row)));
        d.lift();
        // The rows that need a look pop once more as they settle at the top.
        await d.pop(rows.filter(row => row.dataset.stHub !== 'Online').map(row => row.querySelector('.st-dui-tag')), { stagger: 120, duration: 450 });
      }
    },

    // Chat: the carer's message is typed and sent, and Becky's spoken reply arrives as a message.
    chat: {
      length: 10500,
      async play(d) {
        if (d.still) return;   // the still view is the Figma "Chat" screen
        const today = d.$('[data-st-today]');
        const sent = d.$('[data-st-sent]');
        const reply = d.$('[data-st-reply]');
        const field = d.$('[data-st-compose]');
        const placeholder = field.innerHTML;
        const threadTime = d.$('[data-st-thread-time]');
        const threadText = d.$('[data-st-thread-text]');
        const latest = [threadTime.textContent, threadText.textContent];
        // First state: yesterday's exchange is the latest.
        [today, sent, reply].forEach(element => { element.hidden = true; });
        threadTime.textContent = 'Yesterday';
        threadText.textContent = 'Thank you Jane, looking forward to it.';
        await d.wait(500);
        await d.click(field, { x: .15 });
        const words = sent.querySelector('.st-dui-msg__bubble').textContent;
        const typing = document.createElement('span');
        typing.className = 't14';
        field.replaceChildren(typing);
        await d.type(typing, words, { speed: 24 });
        await d.wait(250);
        await d.click(d.$('[data-st-send-button]'));
        field.innerHTML = placeholder;
        today.hidden = sent.hidden = false;
        await Promise.all([d.reveal(today, { duration: 400 }), d.reveal(sent, { y: 16, scale: .97 })]);
        d.lift();
        await d.wait(1400);
        reply.hidden = false;
        await d.reveal(reply, { y: 16, scale: .96, duration: 650, easing: SPRING });
        [threadTime.textContent, threadText.textContent] = latest;
        await d.pop(threadTime, { duration: 400 });
      }
    },

    // Organisational structure: the tree opens to a team, then one organisation's features.
    org: {
      length: 12000,
      async play(d) {
        if (d.still) return;   // the still view is the Figma "Organisational structure" screen
        const unit = name => d.$(`[data-st-unit="${name}"]`);
        const children = name => d.$$(`[data-st-parent="${name}"]`);
        const caret = (row, open) => {
          const icon = row.querySelector('[data-st-caret] .ic');
          icon.classList.toggle('i-org-down', open);
          icon.classList.toggle('i-org-right', !open);
        };
        const london = unit('CareCompany London');
        const agnes = unit('St Agnes Court');
        const toggle = d.$('[data-st-modal] [data-st-toggle]');
        // First state: the region open, its organisations closed, every feature switched on.
        caret(london, false);
        caret(agnes, false);
        const londonRows = [...children('CareCompany London'), ...children('St Agnes Court')];
        londonRows.forEach(row => { row.hidden = true; });
        toggle.classList.add('is-on');
        await d.wait(500);
        await d.click(london.querySelector('[data-st-caret]'));
        caret(london, true);
        const inLondon = children('CareCompany London');
        inLondon.forEach(row => { row.hidden = false; });
        await d.reveal(inLondon, { stagger: 120, y: -8, duration: 450 });
        await d.wait(400);
        await d.click(agnes.querySelector('[data-st-caret]'), { travel: 650 });
        caret(agnes, true);
        const team = children('St Agnes Court');
        team.forEach(row => { row.hidden = false; });
        await d.reveal(team, { y: -8, duration: 450 });
        await d.wait(900);
        // Features are set per organisation and location, and inherited from the parent.
        await d.click(london.querySelector('[data-st-edit]'));
        await d.open(d.$('[data-st-modal]'));
        await d.wait(400);
        await d.click(toggle, { x: .3 });
        toggle.classList.remove('is-on');
        await d.wait(200);
        d.lift();
      }
    }
  };

  class DashReels {
    constructor(tabs) {
      this.tabs = tabs;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.panels = [...tabs.querySelectorAll('[data-st-panel]')];
      this.buttons = [...tabs.querySelectorAll('[data-st-tab]')];
      this.pristine = this.panels.map(panel => panel.querySelector('[data-st-dash-scene]')?.cloneNode(true) || null);
      this.auto = tabs.hasAttribute('data-st-reels-autoplay') && !window.Shopify?.designMode;
      this.resume = this.auto;
      this.current = Math.max(0, this.panels.findIndex(panel => !panel.hidden));
      this.inView = false;
      this.stopped = false;
      if (!reducedMotion()) this.mount(this.current, true);
      this.visual = tabs.querySelector('.st-dash__visual') || tabs;
      this.observer = new IntersectionObserver(entries => {
        const entry = entries[entries.length - 1];
        if (!entry.isIntersecting) this.rest();
        const visible = entry.isIntersecting && entry.intersectionRatio >= .35;
        if (visible === this.inView) return;
        this.inView = visible;
        if (visible) this.play(this.current); else this.stop();
      }, { threshold: [0, .35] });
      this.observer.observe(this.visual);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.rest(); else if (this.inView) this.play(this.current);
      }, { signal });
      motionPreference.addEventListener('change', () => this.play(this.current), { signal });
      // A click on the window stops the reels, each screen on its last frame, and another starts
      // them again.
      this.visual.addEventListener('click', event => {
        if (event.target.closest('button, a')) return;
        this.toggle();
      }, { signal });
      this.switch = document.createElement('button');
      this.switch.type = 'button';
      this.switch.className = 'st-dash-switch';
      this.switch.textContent = 'Stop animation';
      this.switch.addEventListener('click', () => this.toggle(), { signal });
      this.visual.prepend(this.switch);
      this.visual.classList.add('is-reels');
    }
    select(index, source) {
      if (source !== 'auto' && source !== 'init') this.stopAuto();
      const previous = this.current;
      this.current = index;
      this.play(index);
      if (previous === index) return;
      clearTimeout(this.resetTimer);
      const reset = () => this.panels.forEach((panel, i) => { if (i !== this.current) this.mount(i, false); });
      if (reducedMotion()) reset(); else this.resetTimer = setTimeout(reset, 350);
    }
    stopAuto() {
      this.auto = this.resume = false;
      this.progress(-1);
    }
    toggle() {
      if (reducedMotion()) return;
      this.stopped = !this.stopped;
      if (this.stopped) {
        this.resume = this.auto;
        this.auto = false;
      } else this.auto = this.resume;
      this.switch.textContent = this.stopped ? 'Start animation' : 'Stop animation';
      this.tabs.classList.toggle('is-stopped', this.stopped);
      this.play(this.current);
    }
    progress(index, duration = 0) {
      this.buttons.forEach(button => button.querySelector('.st-dash-choice__progress')?.getAnimations().forEach(animation => animation.cancel()));
      const bar = this.buttons[index]?.querySelector('.st-dash-choice__progress');
      if (bar && duration && this.auto) bar.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration, easing: 'linear', fill: 'forwards' });
    }
    mount(index, pending) {
      const panel = this.panels[index];
      const current = panel?.querySelector('[data-st-dash-scene]');
      if (!current || !this.pristine[index]) return null;
      const fresh = this.pristine[index].cloneNode(true);
      fresh.classList.toggle('is-pending', pending);
      current.replaceWith(fresh);
      return fresh;
    }
    stop() {
      this.run?.abort();
      this.progress(-1);
    }
    rest() {
      this.stop();
      if (!reducedMotion() && !this.stopped) this.mount(this.current, true);
    }
    async play(index) {
      this.run?.abort();
      const run = this.run = new AbortController();
      const mode = reducedMotion() ? 'still' : this.stopped ? 'end' : this.inView ? 'play' : null;
      const screen = this.mount(index, mode === 'play' || !mode);
      if (mode !== 'play') this.progress(-1);
      if (!mode) return;
      const reel = screen && reels[screen.dataset.stDashScene];
      if (!reel) return;
      const wait = ms => new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, ms);
        run.signal.addEventListener('abort', () => { clearTimeout(timer); reject(new Cancelled()); }, { once: true });
      });
      try {
        if (mode === 'play') this.progress(index, reel.length + HOLD_MS);
        const started = performance.now();
        await reel.play(director(screen, run.signal, mode));
        if (mode !== 'play') return;
        await wait(Math.max(HOLD_MS, reel.length + HOLD_MS - (performance.now() - started)));
        if (this.auto) this.tabs.selectFeature((index + 1) % this.panels.length);
        else {
          await director(screen, run.signal, 'play').play(screen, [{ opacity: 1 }, { opacity: 0 }], { duration: 400 });
          this.play(index);
        }
      } catch (error) {
        if (!(error instanceof Cancelled)) throw error;
      }
    }
    destroy() {
      clearTimeout(this.resetTimer);
      this.stop();
      this.abort.abort();
      this.observer.disconnect();
      this.switch.remove();
      this.visual.classList.remove('is-reels');
      this.tabs.classList.remove('is-stopped');
    }
  }

  window.SentaiDashReels = {
    attach(tabs) {
      if (tabs.reels || !tabs.selectFeature || !tabs.querySelector('[data-st-dash-scene]') || !('IntersectionObserver' in window) || !CSS.supports('width: 1cqi')) return;
      tabs.reels = new DashReels(tabs);
    }
  };
  // st-theme.js usually connects the tabs first; it attaches any that connect later.
  document.querySelectorAll('st-app-tabs').forEach(tabs => window.SentaiDashReels.attach(tabs));
})();
